import type { Distance, SpatialRectangle } from "@pdfking/math"
import { Max, Min, Scale, Subtract, Total } from "@pdfking/math"
import type { PageShape } from "@pdfking/renderer-api"
import { HorizontalAlign } from "@pdfking/renderer-api"

import { ADDRESS, CONTACTS, PALETTE } from "#constants"
import type { ContentLabels } from "#internal"

import { DrawBadge } from "../layout/DrawBadge"
import { CreateMeasure } from "../text/CreateMeasure"
import { PlainText } from "../text/PlainText"

const MEASURE = CreateMeasure()

/** The contact in `box`, as the shop's calendars close a page: the logo,
 * as tall as the box, on the `right` or the left, and beside it the address
 * and the contacts, two columns of lines, or one where two would not fit;
 * in the paper on a `solid` ground, where a box over twice as tall as wide
 * stacks them under the logo. */
export const DrawContact = ({
  box,
  labels,
  right,
  solid,
}: {
  box: SpatialRectangle<Distance>
  labels: ContentLabels
  right: boolean
  solid: boolean
}): PageShape[] => {
  // A box over twice as tall as wide: the logo at its head, the lines under it
  const stacked = box.height > Scale(box.width, 2)
  // Beside the lines, as tall as the box but leaving them room
  const logo = stacked ? box.width : Min(box.height, Scale(box.width, 0.4))
  const logo_x = right && !stacked ? Subtract(Total(box.x, box.width), logo) : box.x
  const logo_y = stacked ? Subtract(Total(box.y, box.height), logo) : box.y
  const gap = Scale(logo, 0.3)
  const beside = Subtract(box.width, Total(logo, gap))
  const widest = (size: Distance) =>
    Max(...[...ADDRESS, ...CONTACTS].map(line => MEASURE(line, size)))
  // Two columns beside the logo where their lines fit, else one of them all,
  // as tall as the logo
  const paired = Scale(logo, 0.16)
  const single = !stacked && widest(paired) > Scale(beside, 0.5)
  const font = stacked
    ? Scale(box.width, 0.07)
    : single
      ? Min(Scale(logo, 1 / 7.2), Scale(paired, beside / widest(paired)))
      : paired
  const width = stacked || single ? (stacked ? box.width : beside) : Scale(beside, 0.5)
  const text_x = stacked || !right ? (stacked ? box.x : Total(box.x, logo, gap)) : box.x
  const align = right && !stacked ? HorizontalAlign.RIGHT : HorizontalAlign.LEFT
  const top = stacked ? Subtract(logo_y, gap) : Total(box.y, box.height)
  // A column of `lines`, `column` across, or under the other when stacked
  const lines = (values: string[], column: number) =>
    values.map((value, i) =>
      PlainText(
        value,
        stacked || single ? text_x : Total(text_x, Scale(width, right ? 1 - column : column)),
        width,
        Subtract(
          top,
          Scale(
            font,
            1.4 * (i + (stacked ? column * 4 : single ? column * ADDRESS.length : 0)) + 1.2
          )
        ),
        font,
        solid ? PALETTE.paper : column ? PALETTE.secondary : PALETTE.text,
        align
      )
    )
  return [
    ...DrawBadge({
      box: { height: logo, width: logo, x: logo_x, y: logo_y },
      lines: [labels.LOGO_TOP(), labels.LOGO_BOTTOM()],
      spot: true,
    }),
    ...lines(ADDRESS, 0),
    ...lines(CONTACTS, 1),
  ]
}
